import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner
} from "reactstrap";

//i18n
import { withTranslation } from "react-i18next"

import "./index.scss";

import SendModalBody from "./components/SendModalBody";
import RequestModalBody from "./components/RequestModalBody";
import {
  requesResetMessageToaste,
  requestAttachment,
  uploadAttachments,
} from "../../../../../store/attachmentsInteraction/actions";
import { useDispatch, useSelector } from "react-redux";
import { getB64Size, loadFile } from "../../../../../tools/file";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

const AttachmentUploadModal = ({
  t,
  isAttachmentUploadModalOpen,
  setAttachmentUploadModalOpen,
}) => {
  const [isRequestRadioChecked, setIsRequestRadioChecked] = useState(true);
  const [isSendRadioChecked, setIsSendRadioChecked] = useState(undefined); // It needs to be undefined
  const [name, setName] = useState("");
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [note, setNote] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [filesToBeSent, setFilesToBeSent] = useState([]);
  const [isShaking, setIsShaking] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const files = await Promise.all(acceptedFiles.map(loadFile));
      const re = /(?:\.([^.]+))?$/;
      const tempFiles = [];
      for (const file of files) {
        const fileExtension = re.exec(file.name)[1];
        const extensionlessFilename = file.name.replace(fileExtension, "");
        file.name = `${extensionlessFilename}${fileExtension.toLowerCase()}`;
        const newFileName = (name + "_" + file.name)
          .toLowerCase()
          .split(/\b(\s)/)
          .filter(function (entry) {
            return entry.trim() !== "";
          })
          .join("_");
        tempFiles.push({
          fileName: newFileName,
          mimeType: file?.type,
          data: file?.data,
          size: file?.size ?? getB64Size(file?.data),
        });
      }
      setFilesToBeSent(tempFiles);
    })();
  }, [acceptedFiles, name, note]);

  const { currentConversationId, currentTabId, currentConsumerId } =
    useSelector((state) => {
      return state?.ClientInformation;
    });

  const { requestAttachmentLoading = false, message = null, requestAttachmentSuccess} =
    useSelector((state) => {
      return state?.AttachmentsInteraction;
    });

  toastr.options = {
    closeButton: false,
    debug: false,
    newestOnTop: false,
    progressBar: true,
    positionClass: "toast-top-right",
    preventDuplicates: true,
    onclick: null,
    showDuration: "5000",
    hideDuration: "5000",
    timeOut: "5000",
    extendedTimeOut: "5000",
    showEasing: "swing",
    hideEasing: "linear",
    showMethod: "fadeIn",
    hideMethod: "fadeOut",
  };

  // TODO: Improve real time attachment update

  const showToast = (type, message) => {
    setTimeout(() => toastr[type](message));
  };

  const closeModal = useCallback(()=>{
    setAttachmentUploadModalOpen(false);
  },[setAttachmentUploadModalOpen]);

  const handleFinish = () => {
    if (isRequestRadioChecked) {
      dispatch(
        requestAttachment({
          conversationId: currentConversationId,
          tabId: currentTabId,
          name,
          reason: note,
          closeModal,
          t,
        })
      );     
    } else if (isSendRadioChecked) {
      dispatch(
        uploadAttachments({
          consumerId: currentConsumerId,
          conversationId: currentConversationId,
          tabId: currentTabId,
          sendFiles: filesToBeSent,
          note,
          isPrivate: !isPrivate,
          closeModal,
          t,
        })
      );     
    }
  };

  useEffect(() => {
    if (message && requestAttachmentSuccess) {
      showToast("success", message);
      dispatch(requesResetMessageToaste());
    }else if(message && !requestAttachmentSuccess){
      showToast("error", message);
      dispatch(requesResetMessageToaste());
    }
  }, [message]);

  useEffect(() => {
    // IF the modal gets closed, the states are reset
    if (isAttachmentUploadModalOpen === false) {
      setName("");
      setNote("");
      setAcceptedFiles([]);
      setFilesToBeSent([]);
      setIsShaking(false);
      setIsPrivate(false);
      setIsRequestRadioChecked(true);
      setIsSendRadioChecked(undefined);
    }
  }, [isAttachmentUploadModalOpen]);

  return (
    <div>
      <Modal
        isOpen={isAttachmentUploadModalOpen}
        toggle={(_) => {
          setAttachmentUploadModalOpen(false);
        }}
        onAnimationEnd={() => {
          setIsShaking(false);
        }}
        className={"incomplete-modal" ? isShaking : ""}
      >
        <ModalHeader
          toggle={(_) => {
            setAttachmentUploadModalOpen(false);
          }}
        >
          {t('Add new document')}
        </ModalHeader>
        <FormGroup id="attachment-upload-modal-radio-form-group" tag="fieldset">
          <FormGroup style={{ paddingRight: "21px" }} check>
            <Label check>
              <Input
                type="radio"
                name="attachmentRadios"
                checked={isRequestRadioChecked}
                onChange={(event) => {
                  if (event.target.checked) {
                    setIsRequestRadioChecked(true);
                    setIsSendRadioChecked(undefined);
                  }
                }}
              />{" "}
              {t('Request')}
            </Label>
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input
                type="radio"
                name="attachmentRadios"
                checked={isSendRadioChecked}
                onChange={(event) => {
                  if (event.target.checked) {
                    setIsRequestRadioChecked(undefined);
                    setIsSendRadioChecked(true);
                  }
                }}
                maxLength="200"
              />{" "}
              {t('Send')}
            </Label>
          </FormGroup>
        </FormGroup>
        {isRequestRadioChecked && (
          <RequestModalBody setName={setName} setNote={setNote} />
        )}
        {isSendRadioChecked && (
          <SendModalBody
            setName={setName}
            setAcceptedFiles={setAcceptedFiles}
            setNote={setNote}
            setIsPrivate={setIsPrivate}
            isPrivate={isPrivate}
            filesToBeSent={filesToBeSent}
          />
        )}
        <ModalFooter>
          <button
            className="btn btn-outline-primary"
            onClick={(_) => {
              setAttachmentUploadModalOpen(false);
            }}
          >
            {t('Cancel')}
          </button>{" "}
          <Button
            disabled={requestAttachmentLoading}
            color="primary"
            onClick={(_) => {
              if (name?.length > 0 && note?.length > 0) {
                if (filesToBeSent.length === 0 && isSendRadioChecked) {
                  showToast(
                    "error",
                    "Por favor, faça upload de algum arquivo."
                  );
                } else {
                  handleFinish();                  
                  setIsShaking(false);
                }
              } else {
                setIsShaking(true);
                showToast(
                  "error",
                  "Por favor preencha todos os campos obrigatórios."
                );
              }
            }}
          >
            {requestAttachmentLoading && ( 
              <Spinner animation="border" role="status" size="sm" className="mr-2 pt-1">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
            {t('Confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

AttachmentUploadModal.propTypes = {};

export default withTranslation()(AttachmentUploadModal);
